import { FormType } from '@lenderful/domain';

export enum LoanPurpose {
  CONSTRUCTION         = 'CONSTRUCTION',
  REFINANCE            = 'REFINANCE',
  MONTHLY              = 'REFINANCE',
  TERMS                = 'REFINANCE',
  CASHOUT              = 'CASHOUT',
  CONSOLIDATE          = 'CONSOLIDATE',
  LAND_PURCHASE        = 'LAND PURCHASE',
  PURCHASE             = 'PURCHASE',
  HELOC                = 'HELOC',
  PERSONAL_LOAN        = 'UNSECURED',
  AUTO_LOAN            = 'AUTO',
  REVERSE              = 'REVERSE',
  OVERDRAFT_PROTECTION = 'UNSECURED', //@TODO LP-6272
}

export interface RatesRequest {
  escrowsWaived                : boolean;
  creditReportId?              : string;
  fico?                        : string;
  loanAmount                   : number;
  loanPurpose                  : string;
  propertyAppraisedValue       : number;
  propertyType                 : string;
  propertyUsage                : string;
  propertyZip                  : string;
}

export interface Income {
  amount: number;
  type  : string;
}

export interface Liability {
  amount: number;
  type  : string;
}

export interface PrequalRatesRequest extends RatesRequest {
  annualTaxes       : number;
  annualInsurance   : number;
  annualHOA         : number;
  monthlyIncome     : Income[];
  monthlyLiabilities: Liability[];
}

/**
 * @deprecated use `PropertyUsage` in `@lenderful/domain` instead
 */
export type RatesPropertyUsage = 'PRIMARY_RESIDENCE' | 'SECONDARY_OR_VACATION' | 'INVESTMENT_OR_RENTAL';

/**
 * @deprecated use `PropertyType` in `@lenderful/domain` instead
 */
export type RatesPropertyType = 'SINGLE_FAMILY' | 'CONDO' | 'MODULAR' | 'MULTI_DWELLING' | 'MANUFACTURED_HOME_ON_FOUNDATION';

export enum ConsumerLoanRequestType {
  AUTO                 = 'AUTO',
  CREDIT_BOOSTER       = 'CREDIT_BOOSTER',
  OVERDRAFT_PROTECTION = 'OVERDRAFT_PROTECTION',
  PERSONAL             = 'PERSONAL',
}

export interface ConsumerLoanRatesRequest {
  creditReportId?   : number;
  fico?             : string;
  hasUniqueProgram? : boolean;
  loanAmount        : number;
  type              : ConsumerLoanRequestType;
  vehiclePrice?     : number;
  vehicleYear?      : number;
  zipCode           : string;
}

export interface LoanProgramRatesResponse extends Array<LoanProgram> {}

export interface LoanProgram {
  loanAmount                  : number;
  principalAndInterestPayment : number;
  productName                 : string;
  productType                 : string;
  rate                        : number;
  term                        : number;
}

export interface ConsumerLoanLimitsRequest {
  fico : string;
  type : ConsumerLoanRequestType;
}

export interface ConsumerLoanLimitsResponse {
  maxLoanAmount: number;
  minLoanAmount: number;
}

export interface ConsumerLoanDebtToIncomeRequest  {
  creditReportId     : string;
  formName           : FormType;
  monthlyLiabilities : number;
  totalIncome        : number;
  type               : ConsumerLoanRequestType;
}

export interface ConsumerLoanDebtToIncomeResponse {
  isOverLimit: boolean;
}

/**
 * Which reverse mortgage program: pricing-engine's `type` param, also used to record the borrower's
 * selection. HECM is what pricing-engine assumes when no type is sent.
 */
export enum ReverseMortgageProgramType {
  HECM      = 'HECM',
  HIGH_TECH = 'HIGH_TECH',
}

/**
 * Params both reverse mortgage calcs take
 */
interface ReverseMortgageBaseCalcsRequest {
  ageOfYoungest      : number;
  estimatedHomeValue : number;
  mortgageBalance    : number;
  propertyState      : string;
}

/**
 * Params for the HECM calc, which also needs the cash at closing and the payment term (in months)
 */
export interface HecmCalcsRequest extends ReverseMortgageBaseCalcsRequest {
  expectedLumpSum : number;
  term            : number;
}

export interface HecmCalcsResponse {
  initialMortgageInsurance : number;
  maxClaimAmount           : number;
  maxLumpSum               : number;
  netPrincipalLimit        : number;
  tenurePayment            : number;
  termPayment              : number;
  totalUpfrontCosts        : number;
}

/**
 * Params for the High Tech calc, which also needs the payment option (a whole percent). For a
 * purchase, mortgageBalance is always 0 and isPurchase makes pricing-engine apply the whole
 * Maximum Line of Credit toward the purchase at closing instead of a partial initial draw.
 */
export interface HighTechCalcsRequest extends ReverseMortgageBaseCalcsRequest {
  paymentOption : number;
  isPurchase?   : boolean;
}

/**
 * minLoanAmount/maxLoanAmount describe the initial draw, which only applies outside a purchase
 * (see HighTechCalcsRequest.isPurchase); downPaymentRequired is pricing-engine's own down payment
 * figure, and only comes back for a purchase.
 */
export interface HighTechCalcsResponse {
  cappedPayment        : number;
  downPaymentRequired? : number;
  firstMinimumPayment  : number;
  ltv                  : number;
  maxLineOfCredit      : number;
  maxLoanAmount?       : number;
  minLoanAmount?       : number;
}
